import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { AuthService } from '../services/auth.service';
import { IntegrationsService, SftpIntegrationSetup } from './integrations.service';

@Component({
  selector: 'app-sftp-setup-wizard',
  standalone: true,
  imports: [
    CommonModule, RouterModule, ReactiveFormsModule, TranslateModule,
    NzAlertModule, NzButtonModule, NzDescriptionsModule, NzFormModule,
    NzInputModule, NzStepsModule, NzTagModule,
  ],
  templateUrl: './sftp-setup-wizard.component.html',
  styleUrls: ['./integration-setup-wizard.component.less', './sftp-setup-wizard.component.less'],
})
export class SftpSetupWizardComponent implements OnInit {
  item: SftpIntegrationSetup | null = null;
  setupAvailable = false;
  loading = true;
  saving = false;
  step = 0;
  showKeyForm = false;
  keyForm!: FormGroup<{ sshPublicKey: FormControl<string> }>;

  constructor(
    private fb: FormBuilder,
    private integrations: IntegrationsService,
    private auth: AuthService,
    private message: NzMessageService,
    private router: Router,
    private translate: TranslateService,
  ) {
    this.keyForm = this.fb.nonNullable.group({
      sshPublicKey: ['', [Validators.required, Validators.maxLength(16384), sshPublicKeyShapeValidator]],
    });
  }

  ngOnInit(): void {
    this.load();
  }

  get canConfigure(): boolean {
    return this.auth.getCurrentCompanyRole() === 'company_admin'
      && this.auth.hasPermission('company.integrations.credentials.write');
  }

  get isReady(): boolean {
    return !!this.item?.endpoint && ['awaiting_first_file', 'active', 'paused'].includes(this.item.lifecycleStatus);
  }

  get isAwaitingProvisioning(): boolean {
    return this.item?.lifecycleStatus === 'awaiting_provisioning';
  }

  load(): void {
    this.loading = true;
    this.integrations.getSftpIntegration().subscribe({
      next: ({ item }) => {
        this.item = item;
        this.setupAvailable = true;
        this.step = this.isReady ? 3 : (item?.publicKeyConfigured ? 2 : 0);
        this.showKeyForm = false;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.setupAvailable = false;
        this.router.navigate(['/company/integrations']);
      },
    });
  }

  submitKey(): void {
    if (!this.canConfigure) return;
    if (this.keyForm.invalid) {
      this.keyForm.markAllAsTouched();
      return;
    }
    this.saving = true;
    const key = this.keyForm.controls.sshPublicKey.value;
    const request = this.item
      ? this.integrations.replaceSftpPublicKey(this.item.id, key)
      : this.integrations.requestSftpIntegration(key);
    request.subscribe({
      next: ({ item }) => {
        this.item = item;
        this.keyForm.reset();
        this.step = 2;
        this.showKeyForm = false;
        this.saving = false;
        this.message.success(this.translate.instant('INTEGRATION.SFTP.KEY_SAVED'));
      },
      error: (error: { error?: { message?: string } }) => {
        this.saving = false;
        this.message.error(error?.error?.message || this.translate.instant('INTEGRATION.SFTP.ERROR_SAVE'));
      },
    });
  }

  manage(): void {
    if (this.item) this.router.navigate(['/company/integrations', this.item.id, 'status']);
  }

  continueToPublicKey(): void {
    this.step = 1;
    this.showKeyForm = true;
  }

  replaceKey(): void {
    this.showKeyForm = true;
    this.step = 1;
  }

  cancelKeyReplacement(): void {
    this.keyForm.reset();
    this.showKeyForm = false;
    this.step = this.isAwaitingProvisioning ? 2 : (this.isReady ? 3 : 0);
  }
}

function sshPublicKeyShapeValidator(control: AbstractControl<string>): ValidationErrors | null {
  const value = control.value.trim();
  if (!value) return null;
  if (/\r|\n|[\x00-\x1F\x7F]/.test(value)) return { sshPublicKey: true };
  const [algorithm, encoded] = value.split(/\s+/, 3);
  if (!['ssh-ed25519', 'ssh-rsa'].includes(algorithm) || !encoded || !/^[A-Za-z0-9+/]+={0,2}$/.test(encoded)) {
    return { sshPublicKey: true };
  }
  return null;
}
